import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { formatWeekRange, weekStartFor } from '../utils/poa-helpers';
import { buttonClass, IconButton } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { IconChevronLeft, IconChevronRight } from '../components/ui/icons';
import { Appear } from '../components/ui/Appear';

// Idempotent — poa-helpers also extends, but importing this file before
// poa-helpers shouldn't crash. dayjs.extend is a no-op on re-registration.
dayjs.extend(utc);

interface Props {
  weekStart: string;
  onChange: (next: string) => void;
}

/**
 * Compact week navigator: prev / "Week of …" / next, plus a "Today"
 * jumper that only renders when off-current-week.
 */
const WeekNav: React.FC<Props> = ({ weekStart, onChange }) => {
  const today = weekStartFor();
  const isCurrentWeek = weekStart === today;

  const prev = () => onChange(dayjs.utc(weekStart).subtract(7, 'day').format('YYYY-MM-DD'));
  const next = () => onChange(dayjs.utc(weekStart).add(7, 'day').format('YYYY-MM-DD'));

  return (
    <div className="flex items-center gap-1 bg-gray-900/60 rounded-lg p-1 border border-gray-700/40">
      <Tooltip label="Go to the previous week">
        <IconButton onClick={prev} label="Previous week" size="sm">
          <IconChevronLeft />
        </IconButton>
      </Tooltip>

      <div className="px-3 py-1 text-xs font-medium text-on-surface tabular-nums select-none min-w-[150px] text-center">
        Week of {formatWeekRange(weekStart)}
      </div>

      <Tooltip label="Go to the next week">
        <IconButton onClick={next} label="Next week" size="sm">
          <IconChevronRight />
        </IconButton>
      </Tooltip>

      <Appear show={!isCurrentWeek}>
        <button
          type="button"
          onClick={() => onChange(today)}
          className={`${buttonClass('quiet', 'sm')} ml-1`}
        >
          Today
        </button>
      </Appear>
    </div>
  );
};

export default WeekNav;
